// no-dd-sa:typescript-code-style/max-params
import axios, { AxiosInstance } from 'axios';
import type { AuthResponse, Organization, User } from '@/types/core';

declare module 'axios' {
  export interface AxiosRequestConfig {
    /**
     * Opt this request out of the interceptor's hard redirect to /login on an
     * unrecoverable 401. Background bootstrap calls (e.g. /auth/me) set this
     * so a visitor with a stale token on a PUBLIC marketing page isn't yanked
     * to the login screen - the caller clears auth state and ProtectedRoute
     * still redirects on protected routes. Token refresh is attempted either way.
     */
    skipAuthRedirect?: boolean;
  }
}

export type QueryParams = Record<string, string | number | boolean | undefined>;

export const toSnakeCaseKey = (key: string) => key.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);

export const toSnakeParams = (params: Record<string, unknown>) =>
  Object.fromEntries(
    Object.entries(params)
      .filter(([, value]) => value !== undefined)
      .map(([key, value]) => [toSnakeCaseKey(key), value])
  );

/**
 * Recursively converts all object keys from camelCase to snake_case.
 * Used for request bodies to match backend Pydantic schema conventions.
 */
export const toSnakeCaseBody = (data: unknown): unknown => {
  if (data === null || data === undefined) {
    return data;
  }
  if (Array.isArray(data)) {
    return data.map(toSnakeCaseBody);
  }
  if (typeof data === 'object' && !(data instanceof Date) && !(data instanceof File) && !(data instanceof Blob)) {
    return Object.fromEntries(
      Object.entries(data as Record<string, unknown>).map(([key, value]) => [
        toSnakeCaseKey(key),
        toSnakeCaseBody(value),
      ])
    );
  }
  return data;
};

/**
 * Classify an axios error that produced no HTTP response. Returns null when the
 * error has a response (handled by the status-code branches) or is an
 * intentional cancellation (reporting those is noise).
 */
export function classifyRequestFailure(error: {
  code?: string;
  response?: unknown;
}): 'api_timeout' | 'api_network' | null {
  if (error.response != null) return null;
  if (error.code === 'ERR_CANCELED') return null;
  if (error.code === 'ECONNABORTED') return 'api_timeout';
  return 'api_network';
}

// Report each (category|method|url) once per page-session so a bounded-but-
// failing 3s poll doesn't emit dozens of duplicate Sentry events.
const reportedRequestFailures = new Set<string>();

/**
 * Timeout for background PREFETCHES - requests whose result only makes the next
 * interaction faster and whose failure is swallowed.
 *
 * The 30s client default is sized for requests a user is actively waiting on. A
 * prefetch holding a connection for 30s buys nothing: by then the user has
 * either clicked (taking the fetch-on-click path anyway) or left. It also means
 * a stalled proxy produces a 30s hang that reports to Sentry as three separate
 * `api_timeout` issues (CODEPULSE-BACKEND-2YS/2YT/2YV) long after the fact.
 * 8s is well above p99 for these trivial endpoints and fails while the visitor
 * is still on the page.
 */
export const PREFETCH_TIMEOUT_MS = 8000;

export function createApiClient(): AxiosInstance {

  const client = axios.create({
    baseURL: '/api/v1',
    // Hard cap every request. Without a timeout a hung backend/dropped
    // connection leaves the promise pending forever, so a caller's loading
    // state (e.g. the /repositories spinner) never clears and the
    // stuck-spinner watchdog fires. A rejected request instead flows to the
    // caller's .catch/.finally and surfaces an error state. 30s is well
    // above normal p99 latency but below the point a user gives up.
    timeout: 30000,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  // Add auth token to requests and convert camelCase to snake_case
  client.interceptors.request.use((config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // Convert query params from camelCase to snake_case
    if (config.params && typeof config.params === 'object') {
      config.params = toSnakeParams(config.params as Record<string, unknown>);
    }
    // Convert request body from camelCase to snake_case
    // This ensures frontend can use camelCase while backend receives snake_case
    if (config.data && typeof config.data === 'object' && !(config.data instanceof FormData)) {
      config.data = toSnakeCaseBody(config.data);
    }
    return config;
  });

  // Handle auth errors
  client.interceptors.response.use(
    (response) => response,
    async (error) => {
      // 403 = org access denied. Drop the stale org from the store and
      // auto-pick an alternate so the user never lands on a half-broken UI
      // (disabled Settings button, empty dropdowns) after losing access.
      if (error.response?.status === 403) {
        const rawDetail = error.response?.data?.detail;
        const errorDetail = typeof rawDetail === 'string' ? rawDetail : '';
        if (errorDetail.includes("don't have access to this organization")) {
          try {
            const { useOrgStore } = await import('@/store/orgStore');
            useOrgStore.getState().clearCurrentOrg({ autoSelectAlternate: true });
          } catch {
            // Store import failed, fall through
          }
        }
        return Promise.reject(error);
      }

      if (error.response?.status === 401) {
        const rawDetail401 = error.response?.data?.detail;
        const errorDetail = typeof rawDetail401 === 'string' ? rawDetail401 : '';

        // Check if this is a GitHub token issue (not a JWT issue)
        // These errors mean the user needs to re-authenticate with GitHub
        const isGitHubTokenError =
          errorDetail.includes('GitHub token') ||
          errorDetail.includes('login again');

        if (isGitHubTokenError) {
          // GitHub token is invalid/expired - need full re-auth with GitHub
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
          if (error.config?.skipAuthRedirect) {
            // Opted-out bootstrap call (public page): let the caller handle it
            return Promise.reject(error);
          }
          window.location.href = '/login';
          // Return never-resolving promise to prevent code from continuing during redirect
          return new Promise(() => { /* intentionally never resolves */ });
        }

        const refreshToken = localStorage.getItem('refresh_token');
        if (refreshToken) {
          try {
            const { data } = await axios.post('/api/v1/auth/refresh', {
              refresh_token: refreshToken,
            });
            localStorage.setItem('access_token', data.access_token);
            localStorage.setItem('refresh_token', data.refresh_token);

            // Retry original request
            error.config.headers.Authorization = `Bearer ${data.access_token}`;
            return client.request(error.config);
          } catch {
            // Refresh failed - clear tokens and redirect to login
            localStorage.removeItem('access_token');
            localStorage.removeItem('refresh_token');

            if (error.config?.skipAuthRedirect) {
              // Opted-out bootstrap call (public page): reject, don't redirect
              return Promise.reject(error);
            }

            // Capture current location for redirect after login
            const currentPath = window.location.pathname + window.location.search;
            const returnUrl = encodeURIComponent(currentPath);
            window.location.href = `/login?returnUrl=${returnUrl}`;

            // Return a never-resolving promise to prevent calling code from continuing
            // while the redirect is in progress
            return new Promise(() => { /* intentionally never resolves */ });
          }
        } else {
          // No refresh token - redirect to login
          localStorage.removeItem('access_token');

          if (error.config?.skipAuthRedirect) {
            // Opted-out bootstrap call (public page): reject, don't redirect
            return Promise.reject(error);
          }

          const currentPath = window.location.pathname + window.location.search;
          const returnUrl = encodeURIComponent(currentPath);
          window.location.href = `/login?returnUrl=${returnUrl}`;

          // Return a never-resolving promise to prevent calling code from continuing
          return new Promise(() => { /* intentionally never resolves */ });
        }
      }

      // Surface contract bugs (422) and server errors (5xx) to Sentry. A 422
      // on a call from our own frontend usually means the client and server
      // disagree on the request shape (e.g. a param sent in the body instead of
      // the query string) - exactly the class of bug we want to catch before a
      // customer reports it. FastAPI request-validation 422s carry a structured
      // (list) `detail`; an intentional business rejection (e.g. "repo not
      // accessible, grant App access") uses a plain-string `detail` via
      // HTTPException - that's an expected outcome, not a contract bug, so we
      // don't report it. 401/403/404 are handled above or expected too. Sentry
      // is dynamically imported to avoid bundling it eagerly.
      const status = error.response?.status as number | undefined;
      const detail422 = error.response?.data?.detail;
      const isIntentional422 = status === 422 && typeof detail422 === 'string';
      if (!isIntentional422 && (status === 422 || (status != null && status >= 500))) {
        import('@/lib/sentry')
          .then(({ captureError }) => {
            const detail = error.response?.data?.detail;
            captureError(error instanceof Error ? error : new Error(`API ${status} error`), {
              category: 'api_contract',
              url: error.config?.url,
              method: error.config?.method,
              status,
              detail: typeof detail === 'string' ? detail : JSON.stringify(detail ?? null),
            });
          })
          .catch(() => { /* Sentry unavailable - nothing more we can do */ });
      }

      // Timeouts / network failures produce no response and no Sentry event on
      // their own, yet they are what strands a user on a spinner. Report once
      // per endpoint per session.
      const failureCategory = classifyRequestFailure(error);
      if (failureCategory) {
        const method = String(error.config?.method ?? 'get').toUpperCase();
        const url = error.config?.url ?? 'unknown';
        const key = `${failureCategory}|${method}|${url}`;
        if (!reportedRequestFailures.has(key)) {
          reportedRequestFailures.add(key);
          import('@/lib/sentry')
            .then(({ captureRequestFailure }) => {
              captureRequestFailure({ category: failureCategory, method, url, code: error.code });
            })
            .catch(() => { /* Sentry unavailable - nothing more we can do */ });
        }
      }

      return Promise.reject(error);
    }
  );
  return client;
}

export const sharedClient: AxiosInstance = createApiClient();

export abstract class BaseApiClient {
  protected client: AxiosInstance = sharedClient;
}

export class CoreApiClient extends BaseApiClient {
  // Auth
  // `timeoutMs` lets a prefetching caller (LoginPage) fail fast without
  // changing the 30s default for click paths, where the user is waiting and a
  // slow success still beats an error.
  async getGitHubAuthUrl(timeoutMs?: number): Promise<{ authorization_url: string }> {
    const { data } = await this.client.get('/auth/github/authorize', { timeout: timeoutMs });
    return data;
  }
  async getGitHubAuthUrlForSwitchAccount(): Promise<{ authorization_url: string }> {
    const { data } = await this.client.get('/auth/github/authorize/switch-account');
    return data;
  }
  async githubCallback(code: string, state?: string, attribution?: Record<string, string>): Promise<AuthResponse> {
    const { data } = await this.client.post('/auth/github/callback', { code, state, attribution });
    return data;
  }
  async getAdoAuthUrl(): Promise<{ authorization_url: string; state: string }> {
    const { data } = await this.client.get('/auth/ado/authorize');
    return data;
  }
  // ADO callback reuses AuthResponse (same shape as GitHub). For ADO users,
  // user.github_login is nullable (null when no GitHub account is linked).
  async adoCallback(code: string, state?: string, attribution?: Record<string, string>): Promise<AuthResponse> {
    const { data } = await this.client.post('/auth/ado/callback', { code, state, attribution });
    return data;
  }
  async getDevModeStatus(timeoutMs?: number): Promise<{ dev_mode: boolean }> {
    const { data } = await this.client.get('/auth/dev-mode-status', { timeout: timeoutMs });
    return data;
  }
  async devLogin(seed: boolean = true): Promise<AuthResponse> {
    const { data } = await this.client.post('/auth/dev-login', { seed });
    return data;
  }
  async getCurrentUser(): Promise<User> {
    // skipAuthRedirect: this is the app-shell bootstrap call - a stale token
    // on a public marketing page must not hard-redirect the visitor to /login.
    const { data } = await this.client.get('/auth/me', { skipAuthRedirect: true });
    return data;
  }
  // Organizations
  async getOrganizations(page = 1, pageSize = 50) {
    const { data } = await this.client.get<{ organizations: Organization[]; total: number; page: number; page_size: number }>('/organizations', {
      params: { page, pageSize },
    });
    return data;
  }
  async getOrganization(id: string): Promise<Organization> {
    const { data } = await this.client.get(`/organizations/${id}`);
    return data;
  }
  async createOrganization(org: Partial<Organization>): Promise<Organization> {
    const { data } = await this.client.post('/organizations', org);
    return data;
  }
  async updateOrganization(id: string, org: Partial<Organization>): Promise<Organization> {
    const { data } = await this.client.patch(`/organizations/${id}`, org);
    return data;
  }
  // Deletion is asynchronous: the backend creates an offboarding job (revokes
  // external tokens, suppresses emails, then cascades) and returns 202 with the
  // job id. Callers must NOT assume the org is gone on resolve; poll the job or
  // refresh the org list.
  async deleteOrganization(id: string): Promise<{ job_id: string; status: string }> {
    const { data } = await this.client.delete(`/organizations/${id}`);
    return data;
  }
  async getMyOrgLimit(): Promise<{
    regular_orgs: { can_create: boolean; current_count: number; max_count: number | null; highest_tier?: string };
    personal_account: { can_create: boolean; current_count: number; max_count: number; reason?: string | null };
  }> {
    const { data } = await this.client.get('/organizations/limits/my-org-limit');
    const regularOrgs = (data?.regular_orgs ?? {}) as Record<string, unknown>;
    const personalAccount = (data?.personal_account ?? {}) as Record<string, unknown>;
    const regularMax = regularOrgs['max_count'];
    const personalMax = personalAccount['max_count'];
    return {
      regular_orgs: {
        can_create: Boolean(regularOrgs['can_create']),
        current_count: Number(regularOrgs['current_count'] ?? 0),
        max_count: regularMax === null || regularMax === undefined ? null : Number(regularMax),
        highest_tier: typeof regularOrgs['highest_tier'] === 'string' ? regularOrgs['highest_tier'] : undefined,
      },
      personal_account: {
        can_create: Boolean(personalAccount['can_create']),
        current_count: Number(personalAccount['current_count'] ?? 0),
        max_count: Number(personalMax ?? 0),
        reason: typeof personalAccount['reason'] === 'string' ? personalAccount['reason'] : null,
      },
    };
  }
}

export const api = new CoreApiClient();
